import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/sonner';
import { messageErreur } from '../../lib/erreurs';
import { optionsLibelles } from '../../lib/libelles';
import { lotsAPI, nouvelleCleLot } from '../../lib/lotsApi';
import { PRODUITS_CAMION } from '../../lib/lotsCollage';

// Radix refuse une valeur vide: « aucun produit » a sa propre valeur.
const AUCUN = 'aucun';

const depuisLigne = (ligne) => ({
  tractor_plate: ligne?.tractor_plate || '',
  tank_plate: ligne?.tank_plate || '',
  driver_phone: ligne?.driver_phone || '',
  driver_name: ligne?.driver_name || '',
  expected_quantity:
    ligne?.expected_quantity === null || ligne?.expected_quantity === undefined ? '' : String(ligne.expected_quantity),
  product: ligne?.product || AUCUN,
});

/** Corrige un camion attribué, tant qu'il n'est pas confirmé. */
export const ModifierCamionDialog = ({ open, onOpenChange, lotId, ligne, onDone }) => {
  const [valeurs, setValeurs] = useState(() => depuisLigne(ligne));
  const [enCours, setEnCours] = useState(false);
  const cle = useRef(null);

  useEffect(() => {
    if (!open) return;
    setValeurs(depuisLigne(ligne));
    setEnCours(false);
    cle.current = nouvelleCleLot('lot-camion');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ligne?.id]);

  const changer = (champ, valeur) => setValeurs((avant) => ({ ...avant, [champ]: valeur }));

  const enregistrer = async (evenement) => {
    evenement.preventDefault();
    if (enCours || !valeurs.tractor_plate.trim()) return;
    setEnCours(true);
    try {
      const quantite = String(valeurs.expected_quantity).replace(/\s/g, '').replace(',', '.');
      const mise = await lotsAPI.updateTruck(
        lotId,
        ligne.id,
        {
          tractor_plate: valeurs.tractor_plate.trim(),
          tank_plate: valeurs.tank_plate.trim() || null,
          driver_phone: valeurs.driver_phone.trim() || null,
          driver_name: valeurs.driver_name.trim() || null,
          expected_quantity: quantite === '' ? null : Number(quantite),
          product: valeurs.product === AUCUN ? null : valeurs.product,
        },
        cle.current,
      );
      cle.current = null;
      toast.success('Camion modifié');
      onDone?.(mise);
      onOpenChange(false);
    } catch (err) {
      toast.error(messageErreur(err, "Le camion n'a pas pu être modifié"));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="lot-camion-dialog">
        <DialogHeader>
          <DialogTitle>Modifier le camion</DialogTitle>
          <DialogDescription className="sr-only">Corriger la plaque, le chauffeur ou la quantité prévue.</DialogDescription>
        </DialogHeader>
        <form onSubmit={enregistrer} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="camion-tracteur">Tracteur *</Label>
              <Input
                id="camion-tracteur"
                value={valeurs.tractor_plate}
                onChange={(evenement) => changer('tractor_plate', evenement.target.value)}
                className="input-base font-mono"
                data-testid="camion-tracteur"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="camion-citerne">Citerne</Label>
              <Input
                id="camion-citerne"
                value={valeurs.tank_plate}
                onChange={(evenement) => changer('tank_plate', evenement.target.value)}
                className="input-base font-mono"
                data-testid="camion-citerne"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="camion-telephone">Téléphone du chauffeur</Label>
              <Input
                id="camion-telephone"
                inputMode="tel"
                value={valeurs.driver_phone}
                onChange={(evenement) => changer('driver_phone', evenement.target.value)}
                className="input-base"
                data-testid="camion-telephone"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="camion-nom">Nom du chauffeur</Label>
              <Input
                id="camion-nom"
                value={valeurs.driver_name}
                onChange={(evenement) => changer('driver_name', evenement.target.value)}
                className="input-base"
                data-testid="camion-nom"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="camion-quantite">Quantité prévue (L)</Label>
              <Input
                id="camion-quantite"
                inputMode="decimal"
                value={valeurs.expected_quantity}
                onChange={(evenement) => changer('expected_quantity', evenement.target.value)}
                className="input-base text-right tabular-nums"
                data-testid="camion-quantite"
              />
            </div>
            <div className="space-y-1">
              <Label>Produit</Label>
              <Select value={valeurs.product} onValueChange={(valeur) => changer('product', valeur)}>
                <SelectTrigger className="input-base" aria-label="Produit" data-testid="camion-produit">
                  <SelectValue placeholder="Produit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUCUN}>—</SelectItem>
                  {optionsLibelles('fuelProduct', PRODUITS_CAMION).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={enCours}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !valeurs.tractor_plate.trim()} data-testid="camion-enregistrer">
              {enCours ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ModifierCamionDialog;
